import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { SITE_URL, buildPageStructuredData, getSeoRoute } from '@/lib/seo';

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
};

export default function PageSEO() {
  const location = useLocation();

  useEffect(() => {
    const page = getSeoRoute(location.pathname);
    const image = `${SITE_URL}${page.image ?? '/images/og-image.jpg'}`;

    document.title = page.title;
    upsertMeta('meta[name="description"]', { name: 'description', content: page.description });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: page.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: page.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: page.canonical });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: page.title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: page.description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = page.canonical;

    document.head.querySelectorAll('script[data-page-seo]').forEach((node) => node.remove());
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.dataset.pageSeo = 'true';
    schema.textContent = JSON.stringify(buildPageStructuredData(page)).replace(/</g, '\\u003c');
    document.head.appendChild(schema);
  }, [location.pathname]);

  return null;
}
